/**
* Renders locale deep-content sections (process, checklist, timeline,
* pitfalls, apostille, sources, atomic facts) to lift SSR word-count
* and E-E-A-T signals on /(locale)/services pages.
*/
import type { DeepContent } from "@/data/locale-deep-content";

export function LocaleDeepContentBlock({ content: c }: { content: DeepContent }) {
  return (
    <>
      <section className="mx-auto max-w-5xl px-4 py-12">
        <h2 className="text-2xl font-bold text-foreground">{c.processHeading}</h2>
        <p className="mt-3 text-foreground/85 leading-relaxed">{c.processIntro}</p>
        <ol className="mt-6 grid gap-3 md:grid-cols-2">
          {c.process.map((s) => (
            <li key={s.title} className="rounded-lg border border-border bg-card p-4">
              <p className="font-semibold text-foreground">{s.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-foreground/85">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="border-y border-border bg-muted/30 py-12">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-2xl font-bold text-foreground">{c.checklistHeading}</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {c.checklists.map((cl) => (
              <div key={cl.scenario} className="rounded-lg border border-border bg-card p-5">
                <p className="font-semibold text-foreground">{cl.scenario}</p>
                <ul className="mt-2 space-y-1 text-sm text-foreground/85">
                  {cl.items.map((it) => (
                    <li key={it} className="flex gap-2"><span className="text-[var(--gold)]">•</span><span>{it}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-12">
        <h2 className="text-2xl font-bold text-foreground">{c.timelineHeading}</h2>
        <p className="mt-3 text-foreground/85 leading-relaxed">{c.timelineIntro}</p>
        <ul className="mt-5 divide-y divide-border rounded-lg border border-border bg-card">
          {c.timeline.map((t) => (
            <li key={t.label} className="flex justify-between gap-4 p-3 text-sm">
              <span className="text-foreground/85">{t.label}</span>
              <span className="font-semibold text-foreground text-right">{t.value}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="border-y border-border bg-muted/30 py-12">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-2xl font-bold text-foreground">{c.pitfallsHeading}</h2>
          <div className="mt-6 grid gap-3 md:grid-cols-2">
            {c.pitfalls.map((p) => (
              <div key={p.title} className="rounded-lg border border-border bg-card p-4">
                <p className="font-semibold text-foreground">{p.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-foreground/85">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12" data-speakable>
        <h2 className="text-2xl font-bold text-foreground">{c.apostilleHeading}</h2>
        <p className="mt-3 leading-relaxed text-foreground/90">{c.apostilleBody}</p>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-12">
        <h2 className="text-2xl font-bold text-foreground">{c.atomicHeading}</h2>
        <ul className="mt-4 grid gap-2 md:grid-cols-3">
          {c.atomicFacts.map((f) => (
            <li key={f} className="rounded-lg border border-[var(--gold)]/40 bg-[var(--gold)]/[0.05] p-3 text-sm text-foreground">{f}</li>
          ))}
        </ul>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-14">
        <h2 className="text-2xl font-bold text-foreground">{c.sourcesHeading}</h2>
        <ul className="mt-4 space-y-2 text-sm">
          {c.sources.map((s) => (
            <li key={s.href} className="rounded-lg border border-border bg-card p-3">
              <a href={s.href} target="_blank" rel="noopener noreferrer" className="font-semibold text-[var(--gold)] hover:underline">
                {s.label}
              </a>
              <p className="mt-1 text-foreground/80">{s.note}</p>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
